/**
 * Single source of truth for the r/FromAfar community-partner widgets.
 *
 * The member count used to be hardcoded as '9.6k' in both SubredditWidget and
 * ResponsiveSubredditWidget, so it never moved as the subreddit grew. It is
 * still a constant — but a single one, updated by hand, with the live path
 * (api/subreddit-stats.ts) built and waiting behind `LIVE_STATS_ENABLED`.
 */

export const SUBREDDIT_NAME = 'FromAfar';

/**
 * Last-resort member count, used only when neither the live Reddit API nor the
 * `membersCount` field in the admin dashboard's site settings is available —
 * see useSubredditStats for the full precedence. Editing the count in admin
 * settings is the supported way to change it; this constant is the floor.
 *
 * Last updated: 2026-08-08.
 */
export const MEMBER_COUNT = 11200;

/**
 * Flip to `true` once REDDIT_CLIENT_ID / REDDIT_CLIENT_SECRET are set on Vercel
 * — see api/subreddit-stats.ts. Until then the widgets never call the API, so
 * there is no failing request on every page load; they just render the constant
 * above. Nothing else needs to change.
 */
export const LIVE_STATS_ENABLED = false;

export const SUBREDDIT = {
  name: SUBREDDIT_NAME,
  fullName: `r/${SUBREDDIT_NAME}`,
  description: 'Exploring distant views and remote perspectives together',
  url: `https://reddit.com/r/${SUBREDDIT_NAME}`,
  partnership: 'Community Partner',
} as const;

/**
 * Reddit-style abbreviation: 9,634 -> '9.6k', 1,204,000 -> '1.2m'. Values under
 * 1000 are shown exactly, matching how Reddit renders small subs.
 */
export function formatMemberCount(members: number): string {
  if (!Number.isFinite(members) || members < 0) return formatMemberCount(MEMBER_COUNT);
  if (members < 1000) return String(Math.round(members));
  if (members < 1_000_000) return `${trimZero(members / 1000)}k`;
  return `${trimZero(members / 1_000_000)}m`;
}

/**
 * Parse the admin dashboard's "Members Count Display" field.
 *
 * That field is free text and its own help note offers '11.2k', '500' and
 * '1.5M' as equally valid, so a plain Number() is wrong — it yields NaN for
 * every suffixed form. Accepts a bare count ('16500'), thousands separators
 * ('16,500') and a k/m suffix in either case. Returns null for anything else
 * so the caller can fall back rather than render a nonsense number.
 */
export function parseMemberCount(raw: string): number | null {
  const cleaned = raw.trim().replace(/,/g, '');
  const match = /^(\d+(?:\.\d+)?)\s*([km])?$/i.exec(cleaned);
  if (!match) return null;

  const value = Number(match[1]);
  if (!Number.isFinite(value) || value <= 0) return null;

  const suffix = match[2]?.toLowerCase();
  const multiplier = suffix === 'k' ? 1_000 : suffix === 'm' ? 1_000_000 : 1;
  return Math.round(value * multiplier);
}

/** 9.0 -> '9', 9.63 -> '9.6' — Reddit drops a trailing '.0'. */
function trimZero(value: number): string {
  const rounded = (Math.floor(value * 10) / 10).toFixed(1);
  return rounded.endsWith('.0') ? rounded.slice(0, -2) : rounded;
}
